// ============================================================================
// Shopee — Logistics (ship_order + cancel_order).
//
// Ship order:   `POST /api/v2/logistics/ship_order`
//   Used when the seller hands the parcel to the carrier. Payload carries
//   order_sn + a `pickup` OR `dropoff` block depending on the logistics
//   method. We model the common "dropoff with tracking number" variant that
//   matches Thailand Post / Kerry / Flash patterns.
//
// Cancel order: `POST /api/v2/order/cancel_order`
//   Seller-initiated cancellation. Only valid while the order is `UNPAID`
//   or `READY_TO_SHIP` — once shipped, Shopee returns `error_not_found` OR
//   `error_permission` (depending on the shop's region) which we surface as
//   `PROVIDER_ERROR` with the provider message so the caller can react.
// ============================================================================

import { shopeeApiCall } from './client'
import { ShopeeError } from './types'

// ============================================================================
// Auth context
// ============================================================================

export interface ShopeeAuthContext {
  partnerId: string | number
  partnerKey: string
  accessToken: string
  shopId: string | number
  apiBase?: string
  fetchImpl?: typeof fetch
}

// ============================================================================
// shipShopeeOrder — hand off to the carrier
// ============================================================================

export interface ShipShopeeOrderInput extends ShopeeAuthContext {
  orderSn: string
  trackingNumber: string
  /**
   * The carrier's Shopee-specific shipping-method code (e.g. `"thailand_post"`
   * or `"kerry"`). Shopee validates against the active logistics channels on
   * the shop; invalid codes return `error_shipping_method`.
   */
  carrierShippingMethod: string
}

export async function shipShopeeOrder(input: ShipShopeeOrderInput): Promise<void> {
  if (!input.orderSn) throw new ShopeeError('INVALID_INPUT', 'orderSn is required')
  if (!input.trackingNumber) {
    throw new ShopeeError('INVALID_INPUT', 'trackingNumber is required')
  }
  if (!input.carrierShippingMethod) {
    throw new ShopeeError('INVALID_INPUT', 'carrierShippingMethod is required')
  }

  const body = {
    order_sn: input.orderSn,
    dropoff: {
      tracking_number: input.trackingNumber,
      shipping_method: input.carrierShippingMethod,
    },
  }

  const apiInput: Parameters<typeof shopeeApiCall<unknown>>[0] = {
    apiPath: '/api/v2/logistics/ship_order',
    method: 'POST',
    partnerId: input.partnerId,
    partnerKey: input.partnerKey,
    accessToken: input.accessToken,
    shopId: input.shopId,
    body,
    errorHint: 'order',
    ...(input.apiBase ? { apiBase: input.apiBase } : {}),
    ...(input.fetchImpl ? { fetchImpl: input.fetchImpl } : {}),
  }
  await shopeeApiCall<unknown>(apiInput)
}

// ============================================================================
// cancelShopeeOrder — seller-initiated cancellation
// ============================================================================

export interface CancelShopeeOrderInput extends ShopeeAuthContext {
  orderSn: string
  /**
   * Shopee's documented cancel reasons — `OUT_OF_STOCK`, `UNDELIVERABLE_AREA`,
   * `CUST_CANCEL`, `COD_NOT_SUPPORTED`. We pass through verbatim so callers
   * can pick the most accurate reason for their workflow.
   */
  cancelReason: string
}

export async function cancelShopeeOrder(input: CancelShopeeOrderInput): Promise<void> {
  if (!input.orderSn) throw new ShopeeError('INVALID_INPUT', 'orderSn is required')
  if (!input.cancelReason) throw new ShopeeError('INVALID_INPUT', 'cancelReason is required')

  const body = {
    order_sn: input.orderSn,
    cancel_reason: input.cancelReason,
  }

  const apiInput: Parameters<typeof shopeeApiCall<unknown>>[0] = {
    apiPath: '/api/v2/order/cancel_order',
    method: 'POST',
    partnerId: input.partnerId,
    partnerKey: input.partnerKey,
    accessToken: input.accessToken,
    shopId: input.shopId,
    body,
    errorHint: 'order',
    ...(input.apiBase ? { apiBase: input.apiBase } : {}),
    ...(input.fetchImpl ? { fetchImpl: input.fetchImpl } : {}),
  }
  await shopeeApiCall<unknown>(apiInput)
}
